import { useMemo } from 'react';
import { Modal } from '../ui/Modal.js';
import type { AnalystRunSummary } from '@integrate/contracts';
import {
  computeDiagnostics,
  fmt,
  retainedSourceRows,
  reviewFindings,
  sourceRowDependentChecks,
  type DiagnosticItem,
  type Finding,
  type SeparationState,
} from '@integrate/core';
import { StatTile } from '../ui/StatTile.js';

/**
 * The data-quality report, ported from `renderDiagnostics` (orgchart.html:3277).
 *
 * One addition the single-file app does not need: a note about the source rows. In the app `state.rows`
 * is the spreadsheet the user just dropped in, so every check always ran. Here the raw rows are only
 * retained when the import was asked to keep them — an unmapped column can carry a home address or a
 * bank account — and the checks that read them are silent otherwise.
 *
 * Silent is the problem. "No non-numeric salaries" and "we did not look" are opposite answers for
 * someone deciding whether to trust a cost figure, and an empty array says the first while meaning the
 * second. So the checks that could not run are named.
 *
 * THE REVIEW SITS ABOVE ALL OF IT, and is a different question with a different answer. Diagnostics
 * asks whether the data supports the model; `reviewFindings` asks whether the design agrees with
 * itself. They are both "what is wrong here", which is why they belong in one dialog, and they are
 * never the same finding, which is why they are never in one list.
 */
export function Diagnostics({
  state,
  lastRun,
  onClose,
}: {
  state: SeparationState;
  /**
   * The last scheduled review, when this deployment runs one and this reader may see it.
   *
   * PASSED IN RATHER THAN FETCHED HERE, so this component stays a pure function of a state and the
   * one asynchronous thing on the screen belongs to the caller that already owns the project id and
   * the error handling. `null` covers all three of "no schedule configured", "never run yet" and "a
   * restricted reader the route refuses" — the sections below are the same for all three, because
   * the review they show is computed here and needs no run at all.
   */
  lastRun: AnalystRunSummary | null;
  onClose(): void;
}): JSX.Element {
  const { diagnostics, findings, rows, skipped } = useMemo(() => {
    const retained = retainedSourceRows(state);
    // computeDiagnostics reads state.rows; the loaded state arrives with it empty, so it is filled from
    // whatever the import kept rather than left to report on nothing.
    const withRows: SeparationState = { ...state, rows: retained.rows };
    return {
      diagnostics: computeDiagnostics(withRows),
      // The review reads the design, never the rows, so it is given the state as it arrived.
      findings: reviewFindings(state),
      rows: retained,
      skipped: retained.retained === 0 ? sourceRowDependentChecks(state) : [],
    };
  }, [state]);

  const misstated = findings.filter((f) => f.severity === 'misstated');
  const undecided = findings.filter((f) => f.severity === 'undecided');
  /*
    A contradiction in the design blocks just as hard as a data error, and used not to count here at
    all — so a project whose planned hires had no side and whose exited TSA had no month could read
    "No blocking issues found" while three of its numbers were wrong. The undecided list deliberately
    does NOT count: an unfinished design is the normal state of a project, and a banner that never
    goes green is a banner nobody reads.
  */
  const clean = !diagnostics.errors.length && !diagnostics.warnings.length && !misstated.length;

  return (
    <Modal testId="diagnostics-modal" title="Diagnostics" onClose={onClose}>
          {clean && (
            <div className="diag-ok" data-testid="diag-clean">
              <strong>No blocking issues found.</strong> Review the assumptions below to confirm they
              match your intent.
            </div>
          )}

          {lastRun !== null && <SinceLastReview run={lastRun} />}

          <FindingSection
            title="Fix before this goes out"
            note="Something was recorded and the numbers read it differently, or not at all."
            findings={misstated}
            cls="diag-warn"
            testId="review-misstated"
          />

          {skipped.length > 0 && (
            <div className="diag-section diag-skipped" data-testid="diag-skipped">
              <h4 className="diag-h">
                Not checked <span className="diag-count">{skipped.length}</span>
              </h4>
              <div className="diag-item">
                <div className="diag-detail muted">
                  This project did not retain its source rows, so these checks could not run — they are
                  not reported as clean:
                </div>
                <ul className="diag-list">
                  {skipped.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {rows.retained > 0 && rows.retained < rows.population && (
            <div className="diag-section diag-skipped" data-testid="diag-partial">
              <h4 className="diag-h">Partial source rows</h4>
              <div className="diag-item">
                <div className="diag-detail muted">
                  {fmt(rows.retained)} of {fmt(rows.population)} people carry their original row, so the
                  row-level checks below cover only that part of the population.
                </div>
              </div>
            </div>
          )}

          <Section title="Errors" items={diagnostics.errors} cls="diag-err" testId="diag-section-errors" />
          <Section title="Warnings" items={diagnostics.warnings} cls="diag-warn" testId="diag-section-warnings" />
          <Section title="Assumptions applied" items={diagnostics.assumptions} cls="diag-assume" />
          <Section title="Good to know" items={diagnostics.info} cls="" />

          <FindingSection
            title="Be ready to answer"
            note="Nothing here is wrong. These are the decisions nobody has taken yet."
            findings={undecided}
            cls="diag-assume"
            testId="review-undecided"
          />

          <div className="diag-section">
            <h4 className="diag-h">Data summary</h4>
            {/* No caption passed, so no third row — the only tiles in the app that are two lines. */}
            <div className="stat-grid" data-testid="diag-stats">
              {diagnostics.stats.map((s) => (
                <StatTile key={s.k} label={s.k} value={fmt(s.v)} />
              ))}
            </div>
          </div>
    </Modal>
  );
}

/**
 * What has moved since the last scheduled review.
 *
 * THE ONE THING A RECOMPUTATION CANNOT SAY. Every finding below this line is computed here, live,
 * from the state on screen — so a stored run adds nothing by repeating them. What it adds is the
 * comparison: these three are new since Tuesday, and the two you fixed are gone. That is the whole
 * reason runs are stored, and the only thing this section shows.
 *
 * THE SENTENCES COME FROM THE RUNS, not from the live review, and the first draft had this the
 * other way round. A run reviews the project's DEFAULT scenario while this dialog renders whichever
 * one the reader has OPEN, so resolving a stored id against the findings on screen would caption a
 * finding from one design with a sentence from another whenever those differ — and a resolved
 * finding cannot be looked up at all, because it is absent from today's review by definition. Both
 * lists now carry their own words, taken from whichever run could still see them.
 *
 * WHICH IS ALSO WHY THE SCENARIO IS NAMED. The reader is looking at their own scenario's findings
 * with another scenario's history above them; saying so is the difference between a comparison and
 * a non-sequitur.
 */
function SinceLastReview({ run }: { run: AnalystRunSummary }): JSX.Element {
  const when = new Date(run.ranAt).toLocaleString();

  return (
    <div className="diag-section" data-testid="review-since">
      <h4 className="diag-h">Since the last review</h4>
      {run.comparedTo === null ? (
        <div className="diag-detail muted" data-testid="review-first">
          <strong>{run.scenarioName}</strong> was first reviewed {when}. There is nothing to compare
          it against yet — the next review will say what moved.
        </div>
      ) : (
        <>
          <div className="diag-detail muted">
            <strong>{run.scenarioName}</strong> reviewed {when}, against its review of{' '}
            {new Date(run.comparedTo).toLocaleString()}.
          </div>
          {run.added.length === 0 && run.resolved.length === 0 ? (
            <div className="diag-item">
              <div className="diag-detail muted">Nothing new and nothing resolved between them.</div>
            </div>
          ) : (
            <div className="diag-item">
              {run.added.length > 0 && (
                <>
                  <div className="diag-title">
                    New <span className="diag-count">{run.added.length}</span>
                  </div>
                  <ul className="diag-list" data-testid="review-added">
                    {run.added.map((m) => (
                      <li key={m.id}>{m.title}</li>
                    ))}
                  </ul>
                </>
              )}
              {run.resolved.length > 0 && (
                <>
                  <div className="diag-title">
                    Resolved <span className="diag-count">{run.resolved.length}</span>
                  </div>
                  <ul className="diag-list" data-testid="review-resolved">
                    {run.resolved.map((m) => (
                      <li key={m.id}>{m.title}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * The review's half of the dialog.
 *
 * Keyed by `id` and not by title, which is the whole reason a finding has one: the title carries a
 * count, so a re-render after a decision changes the string and React would tear down and rebuild a
 * row that is the same row.
 */
function FindingSection({
  title,
  note,
  findings,
  cls,
  testId,
}: {
  title: string;
  note: string;
  findings: readonly Finding[];
  cls: string;
  testId: string;
}): JSX.Element | null {
  if (!findings.length) return null;
  return (
    <div className={`diag-section ${cls}`} data-testid={testId}>
      <h4 className="diag-h">
        {title} <span className="diag-count">{findings.length}</span>
      </h4>
      <div className="diag-detail muted">{note}</div>
      {findings.map((f) => (
        <div className="diag-item" key={f.id}>
          <div className="diag-title">{f.title}</div>
          <div className="diag-detail muted">{f.detail}</div>
        </div>
      ))}
    </div>
  );
}

function Section({
  title,
  items,
  cls,
  testId,
}: {
  title: string;
  items: readonly DiagnosticItem[];
  cls: string;
  testId?: string;
}): JSX.Element | null {
  if (!items.length) return null;
  return (
    <div className={`diag-section ${cls}`} data-testid={testId}>
      <h4 className="diag-h">
        {title} <span className="diag-count">{items.length}</span>
      </h4>
      {items.map((it) => (
        <div className="diag-item" key={it.title}>
          <div className="diag-title">{it.title}</div>
          <div className="diag-detail muted">{it.detail}</div>
        </div>
      ))}
    </div>
  );
}
